import React, { useState, useMemo, useCallback } from 'react'
import { Modal } from 'react-bootstrap'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { FaLock } from 'react-icons/fa'
import Cards, { Focused } from 'react-credit-cards-2'
import 'react-credit-cards-2/dist/es/styles-compiled.css'
import PagarLogo from '@resources/imgs/Pagar-1.png'
import * as $Order from '@services/Order'

const SweetAlert = withReactContent(Swal)

// ── Installment configuration (mirrors PagarmeService::buildInstallments) ──
const MAX_INTEREST_FREE_INSTALLMENTS = 3
const MAX_INSTALLMENTS = 12
const MONTHLY_INTEREST_RATE = 1.78 // %

interface PagarmeCheckoutModalProps {
  show: boolean
  onHide: () => void
  orderInternalId: string
  amountInCents: number
  onPaymentSuccess?: () => void
}

interface CardData {
  number: string
  holderName: string
  expMonth: string
  expYear: string
  cvv: string
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Format cents → "R$ 1.234,56" */
const formatBRL = (cents: number): string =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/** Build installment options exactly like the backend */
const buildInstallments = (amountInCents: number) => {
  const options: { number: number; total: number; perInstallment: number; interestFree: boolean }[] = []

  for (let i = 1; i <= MAX_INSTALLMENTS; i++) {
    let total: number
    if (i <= MAX_INTEREST_FREE_INSTALLMENTS) {
      total = amountInCents
    } else {
      const rate = MONTHLY_INTEREST_RATE / 100
      total = Math.round(amountInCents * (1 + rate * i))
    }
    options.push({
      number: i,
      total,
      perInstallment: Math.round(total / i),
      interestFree: i <= MAX_INTEREST_FREE_INSTALLMENTS,
    })
  }
  return options
}

/** Mask card number as groups of 4 */
const maskCardNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 16)
  return digits.replace(/(.{4})/g, '$1 ').trim()
}

/** Detect card brand from first digits */
const detectBrand = (number: string): string | null => {
  const d = number.replace(/\D/g, '')
  if (/^4/.test(d)) return 'Visa'
  if (/^5[1-5]/.test(d) || /^2[2-7]/.test(d)) return 'Mastercard'
  if (/^3[47]/.test(d)) return 'Amex'
  if (/^(636368|438935|504175|451416|509|606282|627780|36|301|3841)/.test(d)) return 'Elo'
  if (/^6(?:011|5)/.test(d)) return 'Discover'
  return null
}

// ── Component ────────────────────────────────────────────────────────────────

const PagarmeCheckoutModal: React.FC<PagarmeCheckoutModalProps> = ({
  show,
  onHide,
  orderInternalId,
  amountInCents,
  onPaymentSuccess,
}) => {
  const [card, setCard] = useState<CardData>({
    number: '',
    holderName: '',
    expMonth: '',
    expYear: '',
    cvv: '',
  })
  const [focused, setFocused] = useState<Focused>('')
  const [installments, setInstallments] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const installmentOptions = useMemo(() => buildInstallments(amountInCents), [amountInCents])
  const cardBrand = useMemo(() => detectBrand(card.number), [card.number])

  /** Combined expiry string for the card visualizer */
  const expiryForCard = useMemo(() => {
    const m = card.expMonth.padStart(2, '0')
    const y = card.expYear
    return m && y ? `${m}/${y}` : m || ''
  }, [card.expMonth, card.expYear])

  const selectedInstallment = useMemo(
    () => installmentOptions.find((o) => o.number === installments) ?? installmentOptions[0],
    [installmentOptions, installments],
  )

  // ── Field handlers ──

  const handleCardNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCard((prev) => ({ ...prev, number: maskCardNumber(e.target.value) }))
    setErrors((prev) => ({ ...prev, number: '' }))
  }, [])

  const handleField = useCallback((field: keyof CardData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    if (field === 'expMonth') value = value.replace(/\D/g, '').slice(0, 2)
    if (field === 'expYear') value = value.replace(/\D/g, '').slice(0, 4)
    if (field === 'cvv') value = value.replace(/\D/g, '').slice(0, 4)
    if (field === 'holderName') value = value.toUpperCase()
    setCard((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }, [])

  // ── Validation ──

  const validate = useCallback((): boolean => {
    const e: Record<string, string> = {}
    const digits = card.number.replace(/\D/g, '')

    if (digits.length < 13 || digits.length > 19) e.number = 'Número do cartão inválido'
    if (!card.holderName.trim()) e.holderName = 'Informe o nome impresso no cartão'
    const month = parseInt(card.expMonth, 10)
    if (!month || month < 1 || month > 12) e.expMonth = 'Mês inválido'
    const year = parseInt(card.expYear, 10)
    if (!year || card.expYear.length !== 4 || year < new Date().getFullYear()) e.expYear = 'Ano inválido'
    if (card.cvv.length < 3) e.cvv = 'CVV inválido'

    setErrors(e)
    return Object.keys(e).length === 0
  }, [card])

  // ── Submit ──

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setSubmitting(true)

    try {
      // Step 1 — Tokenize card at Pagar.me
      const tokenResponse = await $Order.tokenizeCard({
        number: card.number.replace(/\D/g, ''),
        holder_name: card.holderName.trim(),
        exp_month: parseInt(card.expMonth, 10),
        exp_year: parseInt(card.expYear, 10),
        cvv: card.cvv,
      })

      const cardToken = tokenResponse.data.token_id

      // Step 2 — Send token + installments to our API
      await $Order.createTransparentPayment(orderInternalId, {
        card_token: cardToken,
        installments,
      })

      setSubmitting(false)
      onHide()

      SweetAlert.fire({
        title: 'Pagamento realizado!',
        text: 'Seu pagamento foi processado com sucesso.',
        icon: 'success',
        confirmButtonText: 'Ok',
      }).then(() => {
        onPaymentSuccess?.()
      })
    } catch (err: any) {
      setSubmitting(false)
      const message =
        err?.response?.data?.message ||
        'Não foi possível processar o pagamento. Verifique os dados e tente novamente.'

      SweetAlert.fire({
        title: 'Erro!',
        text: message,
        icon: 'error',
        confirmButtonText: 'Ok',
      })
    }
  }

  // ── Shared input style ──
  const inputStyle: React.CSSProperties = {
    height: 38,
    borderRadius: 8,
    border: '1.5px solid #dee2e6',
    fontSize: '0.85rem',
    paddingLeft: 14,
    paddingRight: 14,
    backgroundColor: '#fff',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  }

  // ── Render ──

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop={submitting ? 'static' : true}
      contentClassName="border-0 pagarme-checkout-modal"
      style={{ borderRadius: 16 }}
    >
      {/* ── Custom header hero ── */}
      <div
        style={{
          background: 'linear-gradient(135deg, #FA5A84 0%, #5673b5 100%)',
          borderRadius: '12px 12px 0 0',
          padding: '16px 32px 0',
          position: 'relative',
        }}
      >
        {/* Close button */}
        {!submitting && (
          <button
            type="button"
            onClick={onHide}
            style={{
              position: 'absolute',
              top: 14,
              right: 18,
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: 32,
              height: 32,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '1rem',
              lineHeight: 1,
            }}
            aria-label="Fechar"
          >
            ×
          </button>
        )}

        {/* Pagar.me logo + title row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <img
            src={PagarLogo}
            alt="Pagar.me"
            style={{
              height: 26,
              objectFit: 'contain',
              filter: 'brightness(0) invert(1)',
              opacity: 0.92,
            }}
          />
          <div>
            <h5
              style={{
                color: '#fff',
                fontWeight: 700,
                fontSize: '1rem',
                marginBottom: 1,
                letterSpacing: '0.01em',
              }}
            >
              Pagamento com Cartão de Crédito
            </h5>
            <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.75rem', marginBottom: 0 }}>
              Ambiente seguro — seus dados são criptografados
            </p>
          </div>
        </div>

        {/* Interactive card visualizer — overlaps into the body */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: -36,
            position: 'relative',
            zIndex: 2,
          }}
        >
          <div style={{ transform: 'scale(0.78)', transformOrigin: 'center bottom' }}>
            <Cards
              number={card.number}
              name={card.holderName || 'NOME DO TITULAR'}
              expiry={expiryForCard}
              cvc={card.cvv}
              focused={focused}
              placeholders={{ name: 'NOME DO TITULAR' }}
              locale={{ valid: 'Validade' }}
            />
          </div>
        </div>
      </div>

      <Modal.Body style={{ padding: '58px 32px 8px' }}>
        <form id="pagarme-checkout-form" onSubmit={handleSubmit} autoComplete="off">

          {/* ── Amount hero card ── */}
          <div
            style={{
              background: 'linear-gradient(135deg, #fff5f8 0%, #f0f3ff 100%)',
              border: '1.5px solid #f0d6df',
              borderRadius: 12,
              padding: '10px 16px',
              textAlign: 'center',
              marginBottom: 16,
            }}
          >
            <div
              style={{
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                color: '#9aa0b4',
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              Valor total a pagar
            </div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#FA5A84', lineHeight: 1.1 }}>
              {formatBRL(amountInCents)}
            </div>
          </div>

          {/* ── Card Number ── */}
          <div className="form-group mb-2">
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#495057', marginBottom: 4 }}>
              Número do cartão
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className={`form-control ${errors.number ? 'is-invalid' : ''}`}
                style={{ ...inputStyle, paddingRight: cardBrand ? 80 : 14 }}
                placeholder="0000 0000 0000 0000"
                value={card.number}
                onChange={handleCardNumberChange}
                onFocus={() => setFocused('number')}
                onBlur={() => setFocused('')}
                maxLength={19}
                inputMode="numeric"
                disabled={submitting}
              />
              {cardBrand && (
                <span
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    color: '#fff',
                    background: '#5673b5',
                    borderRadius: 20,
                    padding: '2px 10px',
                  }}
                >
                  {cardBrand}
                </span>
              )}
            </div>
            {errors.number && <div className="invalid-feedback d-block">{errors.number}</div>}
          </div>

          {/* ── Holder Name + CVV ── */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: 12,
              marginBottom: 8,
            }}
          >
            <div className="form-group mb-0">
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#495057', marginBottom: 4 }}>
                Nome impresso no cartão
              </label>
              <input
                type="text"
                className={`form-control ${errors.holderName ? 'is-invalid' : ''}`}
                style={inputStyle}
                placeholder="NOME NO CARTÃO"
                value={card.holderName}
                onChange={handleField('holderName')}
                onFocus={() => setFocused('name')}
                onBlur={() => setFocused('')}
                disabled={submitting}
              />
              {errors.holderName && <div className="invalid-feedback d-block">{errors.holderName}</div>}
            </div>
            <div className="form-group mb-0">
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#495057', marginBottom: 4 }}>
                CVV
              </label>
              <input
                type="text"
                className={`form-control ${errors.cvv ? 'is-invalid' : ''}`}
                style={{ ...inputStyle, textAlign: 'center', letterSpacing: '0.2em' }}
                placeholder="•••"
                value={card.cvv}
                onChange={handleField('cvv')}
                onFocus={() => setFocused('cvc')}
                onBlur={() => setFocused('')}
                maxLength={4}
                inputMode="numeric"
                disabled={submitting}
              />
              {errors.cvv && <div className="invalid-feedback d-block">{errors.cvv}</div>}
            </div>
          </div>

          {/* ── Month + Year + Installments ── */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 2fr',
              gap: 12,
              marginBottom: 8,
            }}
          >
            <div className="form-group mb-0">
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#495057', marginBottom: 4 }}>
                Mês
              </label>
              <input
                type="text"
                className={`form-control ${errors.expMonth ? 'is-invalid' : ''}`}
                style={{ ...inputStyle, textAlign: 'center' }}
                placeholder="MM"
                value={card.expMonth}
                onChange={handleField('expMonth')}
                onFocus={() => setFocused('expiry')}
                onBlur={() => setFocused('')}
                maxLength={2}
                inputMode="numeric"
                disabled={submitting}
              />
              {errors.expMonth && <div className="invalid-feedback d-block">{errors.expMonth}</div>}
            </div>
            <div className="form-group mb-0">
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#495057', marginBottom: 4 }}>
                Ano
              </label>
              <input
                type="text"
                className={`form-control ${errors.expYear ? 'is-invalid' : ''}`}
                style={{ ...inputStyle, textAlign: 'center' }}
                placeholder="AAAA"
                value={card.expYear}
                onChange={handleField('expYear')}
                onFocus={() => setFocused('expiry')}
                onBlur={() => setFocused('')}
                maxLength={4}
                inputMode="numeric"
                disabled={submitting}
              />
              {errors.expYear && <div className="invalid-feedback d-block">{errors.expYear}</div>}
            </div>
            <div className="form-group mb-0">
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#495057', marginBottom: 4 }}>
                Parcelamento
              </label>
              <select
                className="form-control"
                style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
                value={installments}
                onChange={(e) => setInstallments(Number(e.target.value))}
                disabled={submitting}
              >
                {installmentOptions.map((opt) => (
                  <option key={opt.number} value={opt.number}>
                    {opt.number}x de {formatBRL(opt.perInstallment)}
                    {opt.interestFree ? ' (sem juros)' : ` (${formatBRL(opt.total)})`}
                  </option>
                ))}
              </select>
            </div>
          </div>

        </form>
      </Modal.Body>

      {/* ── Footer ── */}
      <div style={{ padding: '12px 28px 18px', borderTop: 'none' }}>
        {/* Submit button — full width with gradient */}
        <button
          type="submit"
          form="pagarme-checkout-form"
          className="btn"
          disabled={submitting}
          style={{
            width: '100%',
            height: 44,
            borderRadius: 12,
            background: submitting
              ? '#c4cad8'
              : 'linear-gradient(90deg, #FA5A84 0%, #5673b5 100%)',
            border: 'none',
            color: '#fff',
            fontWeight: 700,
            fontSize: '1rem',
            letterSpacing: '0.02em',
            boxShadow: submitting ? 'none' : '0 4px 14px rgba(86,115,181,0.35)',
            transition: 'opacity 0.2s, box-shadow 0.2s',
            marginBottom: 10,
          }}
        >
          {submitting ? (
            <>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: 8 }} />
              Processando...
            </>
          ) : (
            <>
              <FaLock style={{ marginRight: 8, fontSize: '0.85rem' }} />
              Pagar {formatBRL(selectedInstallment?.total ?? amountInCents)}
            </>
          )}
        </button>

        {/* Security micro-text */}
        <div className="text-center" style={{ fontSize: '0.75rem', color: '#adb5bd', marginBottom: 8 }}>
          <FaLock style={{ marginRight: 4, fontSize: '0.65rem' }} />
          Pagamento seguro e criptografado · Pagar.me
        </div>

        {/* Cancel link */}
        <div className="text-center">
          <button
            type="button"
            onClick={onHide}
            disabled={submitting}
            style={{
              background: 'none',
              border: 'none',
              color: '#9aa0b4',
              fontSize: '0.82rem',
              cursor: submitting ? 'not-allowed' : 'pointer',
              textDecoration: 'underline',
              padding: 0,
            }}
          >
            Cancelar e voltar
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default PagarmeCheckoutModal
